import { css } from '@emotion/css';
import { Typography } from '@mui/material';

// HELPERS ------------------------------------------------------------------- //

function getBatteryColor(level: number): string {
  if (level >= 0.5) return '#22c55e';  // green
  if (level >= 0.2) return '#fbbf24';  // yellow
  return '#ef4444';                     // red
}

// TYPES --------------------------------------------------------------------- //

interface BatteryIndicatorProps {
  /** 0-1 fraction */
  level?: number | null;
  /** Whether the device is charging */
  charging?: boolean | null;
  /** Font size for the percentage label (default 12) */
  fontSize?: number;
  /** Icon height in px (default 13) */
  iconSize?: number;
}

// COMPONENT ----------------------------------------------------------------- //

const BatteryIndicator = ({
  level,
  charging,
  fontSize = 12,
  iconSize = 13,
}: BatteryIndicatorProps) => {
  if (level == null) {
    return (
      <Typography variant='body2' sx={{ fontSize, color: '#fff', fontWeight: 600 }}>
        -
      </Typography>
    );
  }

  const pct = Math.round(level * 100);
  const fill = getBatteryColor(level);
  // Match viewBox aspect ratio (22:13) exactly to avoid stretching
  const iconWidth = Math.round(iconSize * (22 / 13));

  return (
    <div className={cls.wrapper}>
      <svg
        width={iconWidth}
        height={iconSize}
        viewBox='0 0 22 13'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{ display: 'block', flexShrink: 0 }}
      >
        {/* Body outline */}
        <rect x='0.5' y='0.5' width='18' height='12' rx='2' stroke='rgba(255,255,255,0.4)' strokeWidth='1' />
        {/* Fill bar */}
        <rect
          x='1.5' y='1.5' rx='1'
          width={Math.max(0, level * 16)}
          height='10'
          fill={fill}
        />
        {/* Terminal nub */}
        <rect x='19.5' y='3.5' width='2' height='6' rx='0.5' fill='rgba(255,255,255,0.4)' />
        {/* Charging bolt */}
        {charging ? (
          <path
            d='M10 1.5 L7.5 6 L9.5 6 L8 11.5 L12.5 5.5 L10 5.5 Z'
            fill='#fff'
            opacity='0.85'
          />
        ) : null}
      </svg>
      <Typography variant='body2' sx={{ fontSize, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {pct}%
      </Typography>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const cls = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
};

export default BatteryIndicator;
